import {
	subcategoryDisplayLabel,
	type ConfiguratorConfig,
	type LumaSubcategory,
	type LumaSize,
	type LumaOptionGroup,
	type LumaOptionItem,
} from './types';
import { SizeFilter } from './SizeFilter';
import {
	MAT_SIZE_GROUP,
	filterMatItemsForGlass,
	sortOptionGroupsForDisplay,
} from './glassMatFilter';

/**
 * Manages the 4-step guided selection wizard:
 *   Step 1: Product Type (subcategory selection)
 *   Step 2: Size
 *   Step 3: Options (one per option group)
 *   Step 4: Add to Cart (rendered when all options are complete)
 *
 * Each step is rendered as a section within the configurator container.
 * Completing a step unlocks the next one and scrolls it into view.
 */
export class StepManager {

	private container: HTMLElement;
	private config: ConfiguratorConfig;
	private productName: string;

	// Current selections
	selectedSubcategory: LumaSubcategory | null = null;
	selectedSize: LumaSize | null = null;
	selectedOptions: Record<string, number> = {};

	// Event callbacks
	onAllSelectionsComplete?: (subcat: LumaSubcategory, size: LumaSize, options: Record<string, number>) => void;
	onSelectionChange?: () => void;

	constructor(container: HTMLElement, config: ConfiguratorConfig, productName: string) {
		this.container = container;
		this.config    = config;
		this.productName = productName;
	}

	render(): void {
		this.container.innerHTML = '';
		this.renderStep1();
	}

	// --- Step 1: Product Type ---

	private renderStep1(): void {
		const step = this.createStep(1, 'Print Medium');

		const wrap = document.createElement('div');
		wrap.className = 'luma-configurator__select-wrap';

		const select = document.createElement('select');
		select.className = 'luma-configurator__select';
		select.id = 'luma-configurator-step-1-select';
		select.setAttribute('aria-labelledby', 'luma-configurator-step-1-heading');

		const placeholder = document.createElement('option');
		placeholder.value = '';
		placeholder.textContent = 'Select product type';
		placeholder.disabled = true;
		if (!this.selectedSubcategory) {
			placeholder.selected = true;
		}
		select.appendChild(placeholder);

		for (const subcat of this.config.subcategories) {
			const opt = document.createElement('option');
			opt.value = String(subcat.subcategoryId);
			opt.textContent = subcategoryDisplayLabel(subcat);
			if (this.selectedSubcategory?.subcategoryId === subcat.subcategoryId) {
				opt.selected = true;
			}
			select.appendChild(opt);
		}

		select.addEventListener('change', () => {
			const v = select.value;
			if (v === '') {
				return;
			}
			const id = parseInt(v, 10);
			const subcat = this.config.subcategories.find((s) => s.subcategoryId === id);
			if (subcat) {
				this.selectSubcategory(subcat);
			}
		});

		wrap.appendChild(select);
		step.appendChild(wrap);
		this.container.appendChild(step);
	}

	private selectSubcategory(subcat: LumaSubcategory): void {
		this.selectedSubcategory = subcat;
		this.selectedSize        = null;
		// Pre-populate auto-assigned options so they flow into pricing and cart
		// without being shown as customer-facing steps.
		// autoAssignedOptions now carries { optionId, optionName } objects; extract the IDs.
		this.selectedOptions = Object.fromEntries(
			Object.entries(subcat.autoAssignedOptions).map(([group, { optionId }]) => [group, optionId])
		);

		this.render();
		this.renderStep2();
		this.onSelectionChange?.();
	}

	// --- Step 2: Size ---

	private renderStep2(): void {
		if (!this.selectedSubcategory) return;

		const step = this.createStep(2, 'Size');
		const panelCount = Math.max(1, this.config.productionImageCount ?? 1);

		if (panelCount > 1) {
			const note = document.createElement('p');
			note.className = 'luma-configurator__step-note';
			note.textContent = 'Size per panel — each production image is printed at this size.';
			step.querySelector('h3')?.insertAdjacentElement('afterend', note);
		}

		const sizes = this.selectedSubcategory.availableSizes;

		if (sizes.length === 0) {
			const msg = document.createElement('p');
			msg.className = 'luma-configurator__empty-message';
			msg.textContent = 'No standard sizes are available for this product with the current artwork dimensions.';
			step.appendChild(msg);
		} else {
			const wrap = document.createElement('div');
			wrap.className = 'luma-configurator__select-wrap';

			const select = document.createElement('select');
			select.className = 'luma-configurator__select';
			select.id = 'luma-configurator-step-2-select';
			select.setAttribute('aria-labelledby', 'luma-configurator-step-2-heading');

			const placeholder = document.createElement('option');
			placeholder.value = '';
			placeholder.textContent = 'Select size';
			placeholder.disabled = true;
			if (!this.selectedSize) {
				placeholder.selected = true;
			}
			select.appendChild(placeholder);

			for (const size of sizes) {
				const opt = document.createElement('option');
				opt.value = SizeFilter.encodeSizeValue(size);
				opt.textContent = size.belowDpi
					? `${SizeFilter.formatLabel(size)} (lower resolution)`
					: SizeFilter.formatLabel(size);
				if (this.selectedSize && SizeFilter.isSameSize(this.selectedSize, size)) {
					opt.selected = true;
				}
				select.appendChild(opt);
			}

			select.addEventListener('change', () => {
				const v = select.value;
				if (v === '') {
					return;
				}
				const size = SizeFilter.findSizeByValue(sizes, v);
				if (size) {
					this.selectSize(size);
				}
			});

			wrap.appendChild(select);
			step.appendChild(wrap);
		}

		// DPI quality notice (soft DPI mode only).
		if (this.selectedSize?.belowDpi) {
			const warning = document.createElement('p');
			warning.className = 'luma-configurator__dpi-warning';

			const icon = document.createElement('span');
			icon.className = 'luma-configurator__dpi-warning-icon';
			icon.setAttribute('aria-hidden', 'true');
			icon.textContent = '!';

			const text = document.createElement('span');
			text.textContent = 'This size goes beyond the ideal resolution for this image. It will still print beautifully — just best appreciated from a normal viewing distance rather than up close.';

			warning.appendChild(icon);
			warning.appendChild(text);
			step.appendChild(warning);
		}

		// Custom size request link.
		const customLink = document.createElement('p');
		customLink.className = 'luma-configurator__custom-size-link';
		const anchor = document.createElement('a');
		anchor.href = this.buildCustomSizeContactHref();
		anchor.textContent = 'Request a Custom Size';
		customLink.appendChild(anchor);
		step.appendChild(customLink);

		this.container.appendChild(step);

		step.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}

	private buildCustomSizeContactHref(): string {
		const url = new URL(this.config.contactPageUrl, window.location.origin);

		// Param matches the dynamic 'parameter name' in gravity forms
		url.searchParams.set('form_interest', 'custom-sizing');

		const mediumName = this.selectedSubcategory
			? subcategoryDisplayLabel(this.selectedSubcategory)
			: '';
		const productInfoLines = [this.productName, mediumName].filter((v) => v.trim() !== '');
		// Param matches the dynamic 'parameter name' in gravity forms
		url.searchParams.set('form_product_info', productInfoLines.join('\n'));

		return url.toString();
	}

	private selectSize(size: LumaSize): void {
		this.selectedSize = size;
		// Re-seed with auto-assigned options so they are not lost when the size changes.
		// Customer-facing selections (border, finish, etc.) are added on top of this as normal.
		this.selectedOptions = this.selectedSubcategory
			? Object.fromEntries(
					Object.entries(this.selectedSubcategory.autoAssignedOptions)
						.map(([group, { optionId }]) => [group, optionId])
				)
			: {};

		this.rerenderFromStep(2);
		this.onSelectionChange?.();
		this.renderOptionSteps();
	}

	// --- Step 3: Options (one step per option group) ---

	private getDisplayOptionGroups(): LumaOptionGroup[] {
		if (!this.selectedSubcategory) {
			return [];
		}

		return sortOptionGroupsForDisplay(this.selectedSubcategory.optionGroups);
	}

	private renderOptionSteps(): void {
		if (!this.selectedSubcategory || !this.selectedSize) return;

		const groups = this.getDisplayOptionGroups();

		if (groups.length === 0) {
			this.checkCompletion();
			return;
		}

		for (let i = 0; i < groups.length; i++) {
			const group = groups[i];

			// Only render this group's step if all previous groups have been selected.
			const previousGroups = groups.slice(0, i);
			const allPreviousSelected = previousGroups.every((g) => this.selectedOptions[g.groupName] !== undefined);

			if (!allPreviousSelected) break;

			this.renderOptionGroupStep(group, i + 3);
		}
	}

	private renderOptionGroupStep(group: LumaOptionGroup, stepNumber: number): void {
		const existingStep = this.container.querySelector(`[data-step="${stepNumber}"]`);

		if (existingStep) return;

		const step = this.createStep(stepNumber, group.groupName);
		const headingId = `luma-configurator-step-${stepNumber}-heading`;
		const selectId = `luma-configurator-step-${stepNumber}-select`;

		const wrap = document.createElement('div');
		wrap.className = 'luma-configurator__select-wrap';

		const select = document.createElement('select');
		select.className = 'luma-configurator__select';
		select.id = selectId;
		select.setAttribute('aria-labelledby', headingId);

		const placeholder = document.createElement('option');
		placeholder.value = '';
		placeholder.textContent = `Select ${group.groupName}`;
		placeholder.disabled = true;
		if (this.selectedOptions[group.groupName] === undefined) {
			placeholder.selected = true;
		}
		select.appendChild(placeholder);

		const items =
			group.groupName === MAT_SIZE_GROUP && this.selectedSubcategory && this.selectedSize
				? filterMatItemsForGlass(
						this.selectedSize,
						group.items,
						this.selectedSubcategory,
						this.selectedOptions,
					)
				: group.items;

		const selectedId = this.selectedOptions[group.groupName];

		if (
			group.groupName === MAT_SIZE_GROUP &&
			selectedId !== undefined &&
			!items.some((i) => i.optionId === selectedId)
		) {
			delete this.selectedOptions[group.groupName];
		}

		for (const item of items) {
			const opt = document.createElement('option');
			opt.value = String(item.optionId);
			opt.textContent = item.optionName;
			if (this.selectedOptions[group.groupName] === item.optionId) {
				opt.selected = true;
			}
			select.appendChild(opt);
		}

		select.addEventListener('change', () => {
			const v = select.value;
			if (v === '') {
				return;
			}
			const optionId = parseInt(v, 10);
			const item = items.find((i) => i.optionId === optionId);
			if (item) {
				this.selectOption(group, item);
			}
		});

		wrap.appendChild(select);
		step.appendChild(wrap);
		this.container.appendChild(step);
		step.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}

	private selectOption(group: LumaOptionGroup, item: LumaOptionItem): void {
		this.selectedOptions = {
			...this.selectedOptions,
			[group.groupName]: item.optionId,
		};

		this.rerenderFromStep(3);
		this.renderOptionSteps();
		this.onSelectionChange?.();
		this.checkCompletion();
	}

	// --- Completion check ---

	private checkCompletion(): void {
		if (!this.selectedSubcategory || !this.selectedSize) return;

		const allOptionsSelected = this.getDisplayOptionGroups().every(
			(g) => this.selectedOptions[g.groupName] !== undefined,
		);

		if (allOptionsSelected) {
			this.onAllSelectionsComplete?.(
				this.selectedSubcategory,
				this.selectedSize,
				this.selectedOptions,
			);
		}
	}

	// --- Helpers ---

	private createStep(stepNumber: number, label: string): HTMLElement {
		const section = document.createElement('div');
		section.className = 'luma-configurator__step';
		section.dataset['step'] = String(stepNumber);

		const heading = document.createElement('h3');
		heading.className = 'luma-configurator__step-label';
		heading.id = `luma-configurator-step-${stepNumber}-heading`;
		heading.textContent = label;

		const stepIndicator = document.createElement('span');
		stepIndicator.className = 'luma-configurator__step-number';
		stepIndicator.textContent = String(stepNumber);
		heading.prepend(stepIndicator);

		section.appendChild(heading);

		return section;
	}

	/**
	 * Remove all steps from a given step number onwards, then re-render
	 * the step at that number with the current selection reflected.
	 */
	private rerenderFromStep(fromStep: number): void {
		const steps = this.container.querySelectorAll<HTMLElement>('[data-step]');

		for (const step of steps) {
			const stepNum = parseInt(step.dataset['step'] ?? '0', 10);

			if (stepNum >= fromStep) {
				step.remove();
			}
		}

		if (fromStep <= 1) {
			this.renderStep1();
		} else if (fromStep <= 2) {
			this.renderStep2();
		}
	}
}
